import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { productApi } from '@/lib/api/product-api'
import { salesOrderApi } from '@/lib/api/sales-order-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'
import { orderKeys } from '@/hooks/use-sales-order-query'
import { productKeys } from '@/hooks/use-product-query'

export const kiotVietLinkKeys = {
    candidates: (code: string, name: string) =>
        [...productKeys.all, 'kiotviet-candidates', code, name] as const,
}

/** Gợi ý SP ERP cho popup "Nối sản phẩm" — chỉ tải khi popup mở. */
export function useKiotVietCandidates(
    code: string,
    name: string,
    enabled: boolean,
) {
    return useQuery({
        queryKey: kiotVietLinkKeys.candidates(code, name),
        queryFn: async () => {
            const res = await productApi.getKiotVietCandidates(code, name)
            return res.data ?? []
        },
        enabled: enabled && Boolean(code || name),
    })
}

/** Nối dòng KiotViet vào SP ERP. Nối có thể lan sang đơn khác (dòng "Chưa nối"
 *  cùng mã) và đổi mapping SP → làm mới mọi đơn + SP đang cache. */
export function useLinkKiotVietProduct(orderId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            itemId,
            productId,
        }: {
            itemId: string
            productId: string
        }) => salesOrderApi.linkKiotVietProduct(orderId, itemId, productId),
        onSuccess: (res) => {
            void qc.invalidateQueries({ queryKey: orderKeys.all })
            void qc.invalidateQueries({ queryKey: productKeys.all })
            const linked = res.data?.linked_items ?? 1
            toast.success(
                linked > 1
                    ? m.kiotviet_link_success_many({ count: String(linked) })
                    : m.kiotviet_link_success(),
            )
        },
        onError: (err: unknown) => {
            const { title, description } = formatApiError(
                err,
                m.kiotviet_link_error(),
            )
            toast.error(title, { description })
        },
    })
}
